import { GROUND, HUB_Y, POSTS } from '../sim/constants';

export const TAU = Math.PI * 2;
export const HORIZON = 360;

export interface View {
  cw: number;
  ch: number;
  dpr: number;
  s: number;
  ox: number;
  oy: number;
  L: number;
  R: number;
  T: number;
  B: number;
}

/** Fits the playfield (around x 70..730, y 140..520) to the screen. */
export function computeView(cw: number, ch: number, dpr: number): View {
  const s = Math.max(0.1, Math.min(cw / 660, ch / 380));
  const ox = cw / 2 - 400 * s;
  const oy = (ch > cw ? ch * 0.56 : ch / 2) - 320 * s;
  return { cw, ch, dpr, s, ox, oy, L: -ox / s, R: (cw - ox) / s, T: -oy / s, B: Math.max((ch - oy) / s, 500) };
}

export function ell(ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, rot = 0): void {
  ctx.beginPath();
  ctx.ellipse(x, y, Math.max(0.1, rx), Math.max(0.1, ry), rot, 0, TAU);
}

export function fillStroke(ctx: CanvasRenderingContext2D, fill: string, stroke?: string, lw = 3): void {
  ctx.fillStyle = fill;
  ctx.fill();
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = lw;
    ctx.stroke();
  }
}

/** Stable pseudo-random number in 0..1 for decoration. */
export function hash01(i: number): number {
  const x = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

export function hills(
  ctx: CanvasRenderingContext2D, L: number, R: number, base: number, amp: number, freq: number, phase: number, color: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(L - 10, base + 30);
  for (let x = L - 10; x <= R + 10; x += 8) {
    const y = base - amp * (0.55 + 0.45 * Math.sin(x * freq + phase)) - amp * 0.3 * Math.sin(x * freq * 2.7 + phase * 1.9);
    ctx.lineTo(x, y);
  }
  ctx.lineTo(R + 10, base + 30);
  ctx.closePath();
  ctx.fill();
}

export function star5(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string, stroke: string): void {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const rr = i % 2 ? r * 0.45 : r;
    ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
  }
  ctx.closePath();
  fillStroke(ctx, fill, stroke, 1.5);
}

export interface GroundPalette {
  waterTop: string;
  waterBottom: string;
  shimmer: string;
  padTop: string;
  padEdge: string;
  padVein: string;
  post: string;
  postDark: string;
  postCap: string;
  ripple: string;
  snow?: boolean;
}

export function drawWater(ctx: CanvasRenderingContext2D, v: View, t: number, pal: GroundPalette, choppy = 1): void {
  const w = ctx.createLinearGradient(0, HORIZON, 0, v.B);
  w.addColorStop(0, pal.waterTop);
  w.addColorStop(1, pal.waterBottom);
  ctx.fillStyle = w;
  ctx.fillRect(v.L - 2, HORIZON, v.R - v.L + 4, v.B - HORIZON + 2);
  ctx.strokeStyle = pal.shimmer;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  const wspan = v.R - v.L;
  const hspan = Math.max(40, v.B - 380);
  const n = Math.round(26 * choppy);
  for (let i = 0; i < n; i++) {
    const x = v.L + ((i * 137 + t * 10 * choppy * (1 + (i % 3) * 0.4)) % wspan);
    const y = 380 + ((i * 53) % hspan);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 16, y);
    ctx.stroke();
  }
}

export function drawPosts(ctx: CanvasRenderingContext2D, pal: GroundPalette): void {
  for (const x of POSTS) {
    const bottom = 470;
    ell(ctx, x, bottom, 20, 4);
    ctx.strokeStyle = pal.ripple;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = pal.post;
    ctx.fillRect(x - 9, HUB_Y - 10, 18, bottom - HUB_Y + 10);
    ctx.fillStyle = pal.postDark;
    ctx.fillRect(x + 3, HUB_Y - 10, 6, bottom - HUB_Y + 10);
    ell(ctx, x, HUB_Y - 10, 9, 3.5);
    ctx.fillStyle = pal.postCap;
    ctx.fill();
    if (pal.snow) {
      ell(ctx, x, HUB_Y - 13, 11, 5);
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();
    }
  }
}

export function drawPad(ctx: CanvasRenderingContext2D, pal: GroundPalette, ripples: number[]): void {
  for (const p of ripples) {
    ell(ctx, 400, GROUND + 10, 150 + 50 * p, 25 + 9 * p);
    ctx.globalAlpha = 0.6 * (1 - p);
    ctx.strokeStyle = pal.ripple;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
  ell(ctx, 400, GROUND + 11, 152, 26);
  ctx.fillStyle = pal.padEdge;
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(400, GROUND + 5, 150, 24, 0, 1.25, 1.25 + TAU - 0.32);
  ctx.lineTo(400, GROUND + 5);
  ctx.closePath();
  ctx.fillStyle = pal.padTop;
  ctx.fill();
  ctx.strokeStyle = pal.padVein;
  ctx.lineWidth = 1.5;
  for (let a = 1.8; a < 1.25 + TAU - 0.5; a += 0.62) {
    ctx.beginPath();
    ctx.moveTo(400, GROUND + 5);
    ctx.lineTo(400 + Math.cos(a) * 128, GROUND + 5 + Math.sin(a) * 20);
    ctx.stroke();
  }
}

export function drawReeds(
  ctx: CanvasRenderingContext2D, x0: number, n: number, t: number, stem: string, head: string, sway = 5, lean = 0,
): void {
  for (let i = 0; i < n; i++) {
    const x = x0 + i * 13;
    const h = 120 + (i % 2) * 30 + i * 6;
    const sw = Math.sin(t * 1.3 + i * 0.9 + x0) * sway + lean;
    ctx.strokeStyle = stem;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x, 372);
    ctx.quadraticCurveTo(x, 372 - h * 0.6, x + sw, 372 - h);
    ctx.stroke();
    ctx.save();
    ctx.translate(x + sw * 0.92, 372 - h + 18);
    ctx.rotate(sw * 0.012);
    ell(ctx, 0, 0, 4.5, 14);
    ctx.fillStyle = head;
    ctx.fill();
    ctx.restore();
  }
}

export function skyGradient(ctx: CanvasRenderingContext2D, v: View, stops: [number, string][]): void {
  const g = ctx.createLinearGradient(0, Math.min(v.T, 120), 0, HORIZON);
  for (const [o, c] of stops) g.addColorStop(o, c);
  ctx.fillStyle = g;
  ctx.fillRect(v.L - 2, v.T - 2, v.R - v.L + 4, HORIZON - v.T + 4);
}
